import utf8 from "utf8";

export type UrlTransformOptions = {
	path: boolean;
	query: boolean;
}

export function url(url: string|URL, options?: UrlTransformOptions) {
	const u = typeof url === 'string' ? new URL(url) : url;
	options = Object.assign({path: true, query: true}, options);
	return `${u.protocol}//${u.host}${options.path ? u.pathname : ''}${options.query ? u.search : ''}`;
}

export async function fileToDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(reader.result as string);
		reader.onerror = reject;
		reader.readAsDataURL(file as unknown as Blob);
	});
};

export function hexEncode<T = object>(obj: T): string {
	const value = utf8.encode(JSON.stringify(obj));
    let hex: string;
    let result = '';
    for (let i = 0; i < value.length; i++) {
        hex = value.charCodeAt(i).toString(16);
        result += ("000" + hex).slice(-4);
    }
    return result
}

export function hexDecode<T = object>(value: string): T {
    const hexes = value.match(/.{1,4}/g) || [];
    let result = '';
    for (let i = 0; i < hexes.length; i++) {
        result += String.fromCharCode(parseInt(hexes[i], 16));
    }
    return JSON.parse(utf8.decode(result)) as T;
}

export function getInitials(name: string): string {
	const parts = (name ?? '').replace(/@.*/, '').split(/[._\-\s]+/).filter(Boolean);
	if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	return (parts[0]?.slice(0, 2) ?? '??').toUpperCase();
}

export function getFileExtension(filename: string): string {
    const match = /\.([a-z0-9]+)$/i.exec(filename);
    return match ? match[1].toLowerCase() : '';
}

export function fixMarkdown(markdown: string): string {
	return markdown
		.replace(/([^\n])\n^(<[^>]+>)$/gm, '$1\n\n$2')  // ensure blank line before
		.replace(/^(<[^>]+>)$\n([^\n])/gm, '$1\n\n$2');   // ensure blank line after
}

export function downloadFromUrl(url: string|URL, fileName: string) {
	const a = document.createElement('a');
	a.href = typeof url === 'string' ? url : url.toString();
	a.download = fileName;
	document.body.appendChild(a);
	a.click();
	a.remove();
}

export function createExportFilename(extension: string): string {
	return `export_${new Date().toISOString().replace(/\D/g, '')}.${extension}`;
}

export function dateYearsFromNow(yearOffset: number = 0): string {
	const date = new Date();
	date.setFullYear(date.getFullYear() + yearOffset);
	return date.toISOString().split('T')[0];
}


export async function hashItems(...items: any[]) {
	const str = items.map(item => JSON.stringify(item)).join('.');
	const enc = new TextEncoder();
	const hash = await crypto.subtle.digest('SHA-1', enc.encode(str));
	return Array.from(new Uint8Array(hash)).map(v => v.toString(16).padStart(2, '0')).join('');
}

/** Compares slash-separated paths so that folders come before files on every level. */
export function comparePaths(a: string, b: string): number {
	const pathA = a.split('/');
	const pathB = b.split('/');
	for (let i = 0; ; i++) {
		const dirA = i < pathA.length - 1;
		const dirB = i < pathB.length - 1;
		if (dirA !== dirB) return dirA ? -1 : 1;
		const cmp = pathA[i].localeCompare(pathB[i]);
		if (cmp || !dirA) return cmp;
	}
}

/** Number of leading folders two paths have in common. */
export function commonPathLength(a: string[], b: string[]): number {
	let i = 0;
	while (i < a.length && i < b.length && a[i] === b[i]) i++;
	return i;
}
